import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  GET_BROADCAST_LIVE,
  GetBroadcastLiveData,
  GetBroadcastLiveVariables,
  START_LIVE_STREAM,
  StartLiveStreamData,
  StartLiveStreamVariables,
  GoLiveStreamData,
  GoLiveStreamVariables,
  GO_LIVE_STREAM,
  StopLiveStreamData,
  StopLiveStreamVariables,
  STOP_LIVE_STREAM,
  GET_DROPDOWNS,
  GetDropdownsData,
  GetDropdownsVariables
} from '@jwp/ott-common/src/graphql/query/broadcastLive.queries';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { PATH_USER_VIDEOS } from '@jwp/ott-common/src/paths';
import {
  createBroadcastPlayerItem,
  getBroadcastHlsSource,
  getBroadcastVodSourceUrl,
  isBroadcastCompleted,
  isBroadcastCreating,
  isBroadcastCurrentlyLive,
  isBroadcastInPreview,
  isBroadcastReady,
  isBroadcastScheduled,
  isBroadcastStarting,
  isBroadcastStoppedEarly,
  isBroadcastStopping,
  normalizeBroadcastStatus,
  shouldPlayBroadcastStream,
  shouldPlayBroadcastVod,
  isBroadcastStreaming,
  isBroadcastPlayable
} from '@jwp/ott-common/src/utils/broadcastLive';
import BackwardArrow from '@jwp/ott-theme/assets/icons/arrow_backward.svg';
import EditIcon from '@jwp/ott-theme/assets/icons/edit.svg?react';
import InfoIcon from '@jwp/ott-theme/assets/icons/info.svg?react';
import StartPreviewIcon from '@jwp/ott-theme/assets/icons/start_preview_Icon.svg?react';
import Stream from '@jwp/ott-theme/assets/icons/stream.svg?react';
import Circle from '@jwp/ott-theme/assets/icons/circle.svg?react';
import sparking from '@jwp/ott-theme/assets/icons/sparking.gif';
import ReadyLiveIcon from '@jwp/ott-theme/assets/icons/ready_live_icon.svg?react';
import ReadyDoneIcon from '@jwp/ott-theme/assets/icons/Done.svg?react';
import { PERMISSIONS, hasPermission } from '@jwp/ott-common/src/constants';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';

import IconButton from '../../components/IconButton/IconButton';
import Button from '../../components/Button/Button';
import Icon from '../../components/Icon/Icon';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import Player from '../../components/Player/Player';

import styles from './BroadcastLiveDetailPage.module.scss';

/* ─── Inline SVG icons ─────────────────────────────────────────────────────── */

const CopyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M5 15H4.5C3.39543 15 2.5 14.1046 2.5 13V4.5C2.5 3.39543 3.39543 2.5 4.5 2.5H13C14.1046 2.5 15 3.39543 15 4.5V5" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const WarningIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ClockIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

/* ─── Helpers ────────────────────────────────────────────────────────────────── */

const formatLocalDate = (iso?: string | null): string => {
  if (!iso) { return '-'; }
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatLocalTime = (iso?: string | null): string => {
  if (!iso) { return '-'; }
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const formatUtcTime = (iso?: string | null): string => {
  if (!iso) { return '-'; }
  const timePart = iso.split('T')[1];
  return timePart ? timePart.split('.')[0].replace('Z', '') : '-';
};

const formatDvrWindow = (seconds?: number | null): string => {
  if (!seconds) { return 'No DVR'; }
  if (seconds % 3600 === 0) { return `${seconds / 3600} hour${seconds / 3600 > 1 ? 's' : ''}`; }
  return `${Math.round(seconds / 60)} minutes`;
};

/* ─── Row ────────────────────────────────────────────────────────────────────── */

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className={styles.detailItem}>
    <span className={styles.detailLabel}>{label}</span>
    <span className={styles.detailValue}>{children}</span>
  </div>
);

/* ─── Circular ready icon ────────────────────────────────────────────────────── */
const ReadyCircleIcon: React.FC = () => (
  <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.readyCircle}>
    {/* Dashed outer ring */}
    <circle cx="60" cy="60" r="56" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="4 6" />
    {/* Golden ring */}
    <circle cx="60" cy="60" r="44" stroke="var(--primary-btn-color)" strokeWidth="3" />
    {/* Checkmark */}
    <path d="M42 60l12 12 24-24" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ─── Live dot ─────────────────────────────────────────────────────────────── */
const LiveDotIcon: React.FC = () => (
  <span className={styles.liveDot} />
);

/* ═══════════════════════════════════════════════════════════════════════════════
   Main Component
═══════════════════════════════════════════════════════════════════════════════ */

const BroadcastLiveDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['video', 'common']);
  const navigate = useNavigate();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [btnDisabled, setBtnDisabled] = useState(false);

  const user = useAccountStore((state) => state.user);
  const canEdit = hasPermission(user, PERMISSIONS.UPDATE_BROADCAST_LIVE);
  const canDelete = hasPermission(user, PERMISSIONS.DELETE_BROADCAST_LIVE);

  const { data: dropdownData } = useQuery<GetDropdownsData, GetDropdownsVariables>(GET_DROPDOWNS, {
    client: getAuthApolloClient(),
    variables: { ddType: 'ingest_region' },
    errorPolicy: 'all',
  });

  const { data: broadcastData, loading, error, refetch } = useQuery<GetBroadcastLiveData, GetBroadcastLiveVariables>(GET_BROADCAST_LIVE, {
    client: getAuthApolloClient(),
    variables: { blId: id || '' },
    skip: !id,
    errorPolicy: 'all',
  });

  const [startLiveStream] = useMutation<StartLiveStreamData, StartLiveStreamVariables>(START_LIVE_STREAM);
  const [goLiveStream] = useMutation<GoLiveStreamData, GoLiveStreamVariables>(GO_LIVE_STREAM);
  const [stopLiveStream] = useMutation<StopLiveStreamData, StopLiveStreamVariables>(STOP_LIVE_STREAM);

  const stream = broadcastData?.getBroadcastLive?.broadcastLive;

  /* Poll while stream is stopping until status settles */
  // useEffect(() => {
  //   if (normalizeBroadcastStatus(stream?.blStatus) !== 'stopping') return;
  //   const interval = setInterval(() => refetch(), 3000);
  //   return () => clearInterval(interval);
  // }, [stream?.blStatus, refetch]);

  /* Countdown timer */
  useEffect(() => {
    if (!stream?.blStartTime) { return; }
    const startTime = new Date(stream.blStartTime).getTime();
    const countdownStart = startTime - 15 * 60 * 1000;
    let interval: ReturnType<typeof setInterval>;

    const updateTimer = () => {
      const now = Date.now();
      if (now < countdownStart) { setTimeLeft(''); return; }
      const diff = startTime - now;
      if (diff <= 0) { setTimeLeft('00:00'); clearInterval(interval); return; }
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
    };

    interval = setInterval(updateTimer, 1000);
    updateTimer();
    return () => clearInterval(interval);
  }, [stream?.blStartTime]);


  const selectedRegion = useMemo(() => {
    return dropdownData?.getDropdowns?.dropdowns?.find(
      (item) => item.ddValue === broadcastData?.getBroadcastLive?.broadcastLive?.blIngestRegion
    );
  }, [dropdownData, broadcastData]);

  const handleCopy = (value: string | null | undefined, key: string) => {
    if (!value) { return; }
    navigator.clipboard?.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((c) => (c === key ? null : c)), 1500);
  };

  if (loading) { return <LoadingOverlay />; }
  if (error || !stream) { return <div className={styles.errorState}>{t('video:broadcast_live.load_error')}</div>; }

  const statusLabel = stream.blStatusStr || (stream.blStatus ? stream.blStatus.charAt(0).toUpperCase() + stream.blStatus.slice(1) : '-');
  const streamTypeLabel = stream.blStreamType === '247' ? t('video:broadcast_live.always_on') : t('video:broadcast_live.live_event');

  const CopyButton: React.FC<{ value?: string | null; copyKey: string }> = ({ value, copyKey }) => (
    <button type="button" className={styles.copyButton} onClick={() => handleCopy(value, copyKey)} aria-label={t('common:copy', { defaultValue: 'Copy' })}>
      {copiedKey === copyKey ? <span className={styles.copied}>{t('common:copied', { defaultValue: 'Copied' })}</span> : <CopyIcon />}
    </button>
  );

  /* Stream action handlers */
  const handleStartStream = async () => {
    try {
      setBtnDisabled(true);
      await startLiveStream({ client: getAuthApolloClient(), variables: { streamId: stream?.blJwStreamId } });
      refetch();
    } catch (e) { console.error('Error starting stream', e); }
    finally { setBtnDisabled(false); }
  };

  const handleGoLive = async () => {
    try {
      setBtnDisabled(true);
      await goLiveStream({ client: getAuthApolloClient(), variables: { streamId: stream?.blJwStreamId } });
      refetch();
    } catch (e) { console.error('Error going live', e); }
    finally { setBtnDisabled(false); }
  };

  const handleStopStream = async () => {
    try {
      setBtnDisabled(true);
      await stopLiveStream({ client: getAuthApolloClient(), variables: { streamId: stream?.blJwStreamId } });
      refetch();
    } catch (e) { console.error('Error stopping stream', e); }
    finally { setBtnDisabled(false); }
  };

  /* ── Derived booleans (Figma state flow) ── */
  const isCreating = isBroadcastCreating(stream); //
  const isReady = isBroadcastReady(stream); //
  const isStarting = isBroadcastStarting(stream); //
  const isScheduled = isBroadcastScheduled(stream); //
  const isLive = isBroadcastCurrentlyLive(stream); //
  const isPreview = isBroadcastInPreview(stream); //
  const isCompleted = isBroadcastCompleted(stream);
  const isStopping = isBroadcastStopping(stream); //
  const isStoppedEarly = isBroadcastStoppedEarly(stream); //
  const isStreaming = isBroadcastStreaming(stream); //
  const isDeleting = normalizeBroadcastStatus(stream.blStatus) === 'deleting';
  const showVodPlayer = shouldPlayBroadcastVod(stream);
  const showLivePlayer = shouldPlayBroadcastStream(stream);
  const showPlayer = showLivePlayer || showVodPlayer;
  const isPlayable = isBroadcastPlayable(stream);

  const renderStreamPlayer = (src: string, mode: 'live' | 'vod') => {
    const playerItem = createBroadcastPlayerItem(stream, src, mode);
    return (
      <div className={styles.playerWrap}>
        <Player
          item={playerItem as any}
          autostart
          ReConnecting={t('video:reconnecting', { defaultValue: 'Reconnecting...' })}
          disableWatchHistory
        />
      </div>
    );
  };

  const showTimer = !isLive && !isCompleted && !isStoppedEarly && !isStopping && timeLeft && timeLeft !== '00:00';
  const scheduledNote = t('video:broadcast_live.scheduled_note', {
    date: formatLocalDate(stream.blStartTime),
    time: formatLocalTime(stream.blStartTime),
    defaultValue: `Stream scheduled to automatically go live on ${formatLocalDate(stream.blStartTime)} ${formatLocalTime(stream.blStartTime)}`
  });

  /* ── Banner row (top of preview box) ── */
  const renderBanner = () => {
    if (isLive) {
      return (
        <div className={styles.previewBanner}>
          <div className={styles.liveBadge}>
            <LiveDotIcon />
            <span>{t('video:broadcast_live.live', { defaultValue: 'Live' })}</span>
          </div>
          <span className={styles.broadcastingLabel}>{t('video:broadcast_live.currently_broadcasting', { defaultValue: 'Currently Broadcasting' })}</span>
        </div>
      );
    }
    if (isCompleted || isStoppedEarly) {
      return null;
    }

    if (isCreating || isReady || isStarting || isScheduled || isStreaming && stream.blIsLive == false) {
      return (
        <div className={styles.previewBanner}>
          {showTimer && !isScheduled && <span className={styles.timerBadge}>{timeLeft}</span>}
          {isScheduled && <span className={styles.timerBadge}>00:00</span>}
          <span className={styles.scheduledText}>{scheduledNote}</span>
        </div>
      );
    }

    return null
  };


  /* ── Centre area ── */
  const renderCenter = () => {
    const vodSrc = getBroadcastVodSourceUrl(stream);

    // If the broadcast has ended or was stopped early, prefer VOD playback when available
    if (isPlayable && vodSrc) {
      return renderStreamPlayer(vodSrc, 'vod');
    }

    if (isPlayable) {
      const src = getBroadcastHlsSource(stream);
      if (!src) {
        return (
          <div className={styles.previewCenterStop}>
            <div className={styles.iconCircle}>
              <WarningIcon />
            </div>
            <h3>{t('video:broadcast_live.streaming_stopped', { defaultValue: 'Streaming stopped' })}</h3>
            <h3>{t('video:broadcast_live.streaming_not_available', { defaultValue: 'Currently streaming is not available at the moment' })}</h3>
          </div>
        );
      }
      return renderStreamPlayer(src, 'live');
    }

    if (isStoppedEarly) {
      return (
        <div className={styles.previewCenter}>
          <div className={styles.creatingSpinner} />
          <h3>{t('video:broadcast_live.stopping_stream', { defaultValue: 'Stopping stream…' })}</h3>
        </div>
      );
    }

    if (isCreating) {
      return (
        <div className={styles.streamCreationContainer}>
          <div className={styles.imageWrapper}>
            <img src={sparking} alt="Sparking" className={styles.image1} />

            <div className={styles.circleWrapper}>
              <Circle className={styles.image2} />

              <div className={styles.streamWrapper}>
                <Stream className={styles.image3} />
              </div>
            </div>
          </div>

          <h3>{t('video:broadcast_live.creating_stream', { defaultValue: 'Creating this stream.' })}</h3>

          <p>
            {t('video:broadcast_live.creating_stream_note', {
              defaultValue: 'It might be a few minutes you can go live.',
            })}
          </p>
        </div>
      );
    }

    if (isReady) {
      return (
        <div className={styles.streamCreationContainer}>
          <div className={styles.imageWrapper}>
            <img src={sparking} alt="Sparking" className={styles.image1} />

            <div className={styles.circleWrapper}>
              <Circle className={styles.image2} />

              <div className={styles.streamWrapper}>
                <ReadyDoneIcon className={styles.image3} />
              </div>
            </div>
          </div>

          <h3>{t('video:broadcast_live.ready_stream', { defaultValue: 'Ready' })}</h3>
          <p>
            {t('video:broadcast_live.ready_stream_note', {
              defaultValue: 'Click "Start Preview" below to start your stream in\npreview mode before going live',
            })}
          </p>
        </div>
      );
    }

    if (isStarting && stream.blIsLive == false) {
      return (
        <div className={styles.previewCenterStreaming}>
          <div className={styles.creatingSpinner} />
          <h3>{t('video:broadcast_live.starting_preview', { defaultValue: 'Starting Preview Mode' })}</h3>
          <p>{t('video:broadcast_live.starting_preview_note', { defaultValue: 'Check your equipment before you go live' })}</p>
        </div>
      );
    }

    if (isStreaming && stream.blIsLive == false) {
      return (
        <div className={styles.previewCenterStreaming}>
          <div className={styles.creatingSpinner} />
          <h3>{t('video:broadcast_live.starting_preview', { defaultValue: 'Starting Preview Mode' })}</h3>
          <p>{t('video:broadcast_live.starting_preview_note', { defaultValue: 'Check your equipment before you go live' })}</p>
        </div>
      );
    }

    if (isScheduled) {
      return (
        <div className={styles.previewCenter}>
          <div className={styles.iconCircle}>
            <ClockIcon />
          </div>
          <h3>{t('video:broadcast_live.stream_not_started', { defaultValue: 'Stream not started yet' })}</h3>
          <p>{t('video:broadcast_live.stream_scheduled_note', { defaultValue: 'Your broadcast will begin at the scheduled time.' })}</p>
        </div>
      );
    }

    if (isStopping) {
      return (
        <div className={styles.previewCenterStop}>
          <div className={styles.iconCircle}>
            <WarningIcon />
          </div>
          <h3>{t('video:broadcast_live.streaming_stopped', { defaultValue: 'Streaming stopped' })}</h3>
          <p>{t('video:broadcast_live.streaming_not_available', { defaultValue: 'Currently streaming is not available at the moment' })}</p>
        </div>
      );
    }

    return null;
  };

  /* ── Footer row ── */
  const renderFooter = () => {
    if (isStreaming && stream.blIsLive == false) {
      return (
        <div className={styles.previewFooter}>
          <div className={styles.footerNote}>
            <Icon icon={InfoIcon} className={styles.footerInfoIcon} />
            <span>{t('video:broadcast_live.audience_note')}</span>
          </div>
          <Button
            onClick={handleGoLive}
            disabled={btnDisabled}
            label={t('video:broadcast_live.go_live_now')}
            variant="contained"
            color="primary"
            className={classNames(styles.actionButton, styles.goLiveButton)}
          />
        </div>
      );
    }

    if (isScheduled || isCreating) {
      return (
        <div className={styles.previewFooter}>
          <div className={styles.footerNote}>
            <Icon icon={InfoIcon} className={styles.footerInfoIcon} />
            <span>{t('video:broadcast_live.audience_note')}</span>
          </div>
          {isCreating && (
            <Button label={t('video:broadcast_live.start_preview')} variant="outlined" disabled className={styles.actionButton} />
          )}
        </div>
      );
    }

    if (isReady) {
      return (
        <div className={styles.previewFooter}>
          <div className={styles.footerNote}>
            <Icon icon={InfoIcon} className={styles.footerInfoIcon} />
            <span>{t('video:broadcast_live.audience_note')}</span>
          </div>
          <Button
            onClick={handleStartStream}
            disabled={btnDisabled}
            label={t('video:broadcast_live.start_preview')}
            variant="outlined"
            className={styles.actionButton}
          />
        </div>
      );
    }

    if (isStarting && stream.blIsLive == false) {
      return (
        <div className={styles.previewFooter}>
          <div className={styles.footerNote}>
            <Icon icon={InfoIcon} className={styles.footerInfoIcon} />
            <span>{t('video:broadcast_live.audience_note')}</span>
          </div>
          <Button
            label={t('video:broadcast_live.go_live_now')}
            variant="contained"
            color="primary"
            disabled
            className={classNames(styles.actionButton, styles.goLiveButton)}
          />
        </div>
      );
    }

    if (isLive) {
      return (
        <div className={styles.previewFooter}>
          <div className={styles.footerNote}>
            <Icon icon={InfoIcon} className={styles.footerInfoIcon} />
            <span>{t('video:broadcast_live.audience_note_stop', { defaultValue: 'Audience can now access and watch the live stream.' })}</span>
          </div>
          <Button
            onClick={handleStopStream}
            disabled={btnDisabled}
            label={t('video:broadcast_live.stop')}
            variant="outlined"
            className={styles.stopButton}
          />
        </div>
      );
    }

    const vodSrc = getBroadcastVodSourceUrl(stream);

    if (isPlayable && vodSrc) {
      return (
        <div className={styles.previewFooter}>
          <div className={styles.footerNote}>
            <Icon icon={InfoIcon} className={styles.footerInfoIcon} />
            <span>{t('video:broadcast_live.audience_note_stoped')}</span>
          </div>
        </div>
      );
    }

    if (isStopping) {
      return (
        <div className={styles.previewFooter}>
          <div className={styles.footerNote}>
            <Icon icon={InfoIcon} className={styles.footerInfoIcon} />
            <span>{t('video:broadcast_live.audience_no_longer', { defaultValue: 'Audience can no longer watch this live stream.' })}</span>
          </div>
        </div>
      );
    }

    if ((isStoppedEarly || isStopping) && showVodPlayer) {
      return (
        <div className={styles.previewFooter}>
          <div className={styles.footerNote}>
            <Icon icon={InfoIcon} className={styles.footerInfoIcon} />
            <span>{t('video:broadcast_live.audience_no_longer', { defaultValue: 'Audience can no longer watch this live stream.' })}</span>
          </div>
        </div>
      );
    }

    if (isStoppedEarly) {
      return (
        <div className={styles.previewFooter}>
          <div className={styles.footerNote}>
            <Icon icon={InfoIcon} className={styles.footerInfoIcon} />
            <span>{t('video:broadcast_live.audience_no_longer', { defaultValue: 'Audience can no longer watch this live stream.' })}</span>
          </div>
        </div>
      );
    }

    return null;
  };

  /* ═══════════════════════════════════════════════════════════════
     JSX
  ═══════════════════════════════════════════════════════════════ */
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <IconButton className={styles.iconButton} onClick={() => navigate(`${PATH_USER_VIDEOS}?tab=broadcasts`)}>
            <img src={BackwardArrow} alt={t('common:back', { defaultValue: 'Back' })} />
          </IconButton>
          <h1>{stream.blTitle}</h1>
        </div>
        {canEdit && (
          <Button disabled label={t('video:broadcast_live.edit_stream')} startIcon={<Icon icon={EditIcon} />} variant="outlined" className={styles.editButton} />
        )}
      </header>

      <div className={styles.content}>
        {/* ── LEFT COLUMN ── */}
        <div className={styles.leftColumn}>
          <div className={classNames(styles.previewBox, { [styles.previewBoxLive]: isLive })}>
            {renderBanner()}
            <div className={classNames(styles.previewBody, { [styles.previewBodyWithPlayer]: showPlayer })}>{renderCenter()}</div>
            {renderFooter()}
          </div>

          {/* Playout URLs */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>{t('video:broadcast_live.playout_url')}</h2>
            <div className={styles.copyRow}>
              <span className={styles.copyRowLabel}>{t('video:broadcast_live.hls_playout_url')}</span>
              <div className={styles.copyInput}>
                <span className={styles.copyText}>{stream.blHlsPlayoutUrl || '-'}</span>
                <CopyButton value={stream.blHlsPlayoutUrl} copyKey="hls" />
              </div>
            </div>
            <div className={styles.copyRow}>
              <span className={styles.copyRowLabel}>{t('video:broadcast_live.dash_playout_url')}</span>
              <div className={styles.copyInput}>
                <span className={styles.copyText}>{stream.blDashPlayoutUrl || '-'}</span>
                <CopyButton value={stream.blDashPlayoutUrl} copyKey="dash" />
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className={styles.rightColumn}>
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>{t('video:broadcast_live.stream_details')}</h2>

            <Row label={t('video:broadcast_live.stream_type')}>{streamTypeLabel}</Row>

            <div className={styles.tripleRow}>
              <Row label={t('video:broadcast_live.start_date')}>{formatLocalDate(stream.blStartTime)}</Row>
              <Row label={t('video:broadcast_live.local_time')}>{formatLocalTime(stream.blStartTime)}</Row>
              <Row label={t('video:broadcast_live.utc')}>{formatUtcTime(stream.blStartTime)}</Row>
            </div>

            <div className={styles.tripleRow}>
              <Row label={t('video:broadcast_live.end_date')}>{formatLocalDate(stream.blEndTime)}</Row>
              <Row label={t('video:broadcast_live.local_time')}>{formatLocalTime(stream.blEndTime)}</Row>
              <Row label={t('video:broadcast_live.utc')}>{formatUtcTime(stream.blEndTime)}</Row>
            </div>

            <div className={styles.tripleRow}>
              <Row label={t('video:broadcast_live.status')}>
                <span className={styles.statusValue}>
                  <span className={classNames(styles.statusDot, { [styles.statusStopped]: isStoppedEarly || isCompleted })} />
                  {statusLabel}
                </span>
              </Row>
              <Row label={t('video:broadcast_live.ingest_region')}>{stream.blIngestRegion && selectedRegion?.ddLabel ? selectedRegion?.ddLabel : '-'}</Row>
              <Row label={t('video:broadcast_live.ingest_format')}>{(stream.blIngestFormat || '-').toUpperCase()}</Row>
            </div>

            <div className={styles.tripleRow}>
              <Row label={t('video:broadcast_live.dvr_window')}>{formatDvrWindow(stream.blDvrWindow)}</Row>
              <Row label={t('video:broadcast_live.clipping_window')}>{stream.blClippingWindowDisplayName || '-'}</Row>
              <Row label={t('video:broadcast_live.frame_rate')}>{stream.blFrameRate || '-'}</Row>
            </div>

            <div className={styles.copyRow}>
              <span className={styles.copyRowLabel}>{t('video:broadcast_live.stream_id')}</span>
              <div className={styles.copyInput}>
                <span className={styles.copyText}>{stream.blJwStreamId || '-'}</span>
                <CopyButton value={stream.blJwStreamId} copyKey="streamId" />
              </div>
            </div>

            <div className={styles.copyRow}>
              <span className={styles.copyRowLabel}>{t('video:broadcast_live.stream_url')}</span>
              <div className={styles.copyInput}>
                <span className={styles.copyText}>{stream.blStreamUrl || '-'}</span>
                <CopyButton value={stream.blStreamUrl} copyKey="streamUrl" />
              </div>
            </div>

            <div className={styles.copyRow}>
              <span className={styles.copyRowLabel}>{t('video:broadcast_live.stream_key')}</span>
              <div className={styles.copyInput}>
                <span className={styles.copyText}>{stream.blStreamKey || '-'}</span>
                <CopyButton value={stream.blStreamKey} copyKey="streamKey" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {canDelete && (
        <div className={classNames(styles.card, styles.deleteCard)}>
          <div>
            <h2 className={styles.cardTitle}>{t('video:broadcast_live.delete_stream')}</h2>
            <p className={styles.deleteText}>{t('video:broadcast_live.delete_stream_note')}</p>
          </div>
          <Button disabled label={t('video:broadcast_live.delete_stream')} variant="outlined" color="delete" className={styles.deleteButton} />
        </div>
      )}
    </div>
  );
};

export default BroadcastLiveDetailPage;
